import { registerComponent, storeNumbersMatch, loading } from "helpers";
import WYATTElement from "wyatt";

registerComponent(
  "store-list",
  class StoreList extends WYATTElement {
    static cache = new Map();

    abortController = null;

    constructor() {
      super();

      this.resultsCount = this.querySelector("[data-count]");
      this.resultsType = this.querySelector("[data-type]");
      this.resultsLocation = this.querySelector("[data-location]");
      this.resultsList = this.querySelector("[data-results]");
      this.onRender = this.render.bind(this);
    }

    connectedCallback() {
      this.subscribe(WYATT.PubSub.events.storesSorted, this.onRender);
      this.subscribe(WYATT.PubSub.events.storesFiltered, this.onRender);
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.abortController?.abort();
    }

    get count() {
      return this.stores.length;
    }

    get storeTypes() {
      return WYATT?.storeLocator?.storeTypes || ["traditional"];
    }

    get storeTypeCopy() {
      if (this.storeTypes.length > 1) {
        return this.config.t.stores;
      }

      switch (this.storeTypes[0]) {
        case "traditional":
          return this.config.t.full_service_stores;
        case "express":
          return this.config.t.express_stores;
        default:
          return this.config.t.stores;
      }
    }

    get stores() {
      const stores = [
        ...WYATT.Geolocation.getStoresWithinRadius(
          this.config.radius,
          this.storeTypes,
        ),
      ];
      const preferred = WYATT.PreferredStores?.store;

      if (!preferred) return stores;

      const index = stores.findIndex((store) =>
        storeNumbersMatch(store.store_number, preferred.store_number),
      );

      if (index <= 0) return stores;

      const [preferredStore] = stores.splice(index, 1);
      stores.unshift(preferredStore);

      return stores;
    }

    async render() {
      this.abortController?.abort();
      this.abortController = new AbortController();

      const { signal } = this.abortController;

      this.removeAttribute("store-list-ready");
      await this.renderList(signal);

      if (signal.aborted) {
        return;
      }

      this.renderCount();
      this.setAttribute("store-list-ready", "");
    }

    renderCount() {
      this.resultsCount.textContent = this.count;
      this.resultsType.textContent = this.storeTypeCopy;
      this.resultsLocation.textContent =
        WYATT.Geolocation.location.address?.formatted ??
        this.config.t.your_location;
    }

    async renderList(signal) {
      this.resultsList.innerHTML = "";
      loading.add(this.resultsList);

      try {
        const { stores } = this;

        const storeLocations = await Promise.all(
          stores.map((store) => this.getStoreLocation(store, signal)),
        );

        if (signal.aborted) {
          return;
        }

        storeLocations.forEach((storeLocation) => {
          if (storeLocation) {
            this.resultsList.appendChild(storeLocation);
          }
        });
      } finally {
        if (!signal.aborted) {
          loading.remove(this.resultsList);
        }
      }
    }

    async getStoreLocation(store, signal) {
      const { handle } = store;
      const { cache } = StoreList;

      if (cache.has(handle)) {
        return cache.get(handle).cloneNode(true);
      }

      try {
        const response = await fetch(
          `${WYATT.routes.root}pages/store/${handle}?view=location`,
          { signal },
        );

        if (!response.ok) {
          return null;
        }

        const html = await response.text();
        const storeLocation = document.createElement("div");

        storeLocation.classList.add("store-list__result");
        storeLocation.innerHTML = html;

        cache.set(handle, storeLocation);

        return storeLocation.cloneNode(true);
      } catch (error) {
        if (error.name === "AbortError") return null;

        throw error;
      }
    }
  },
);
